import * as React from 'react';
import {
    Typography,
    Box,
    Container,
    Button,
    CircularProgress,
    Autocomplete,
    Chip,
} from '@mui/material';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { centered_flex_box, MainInput, main_button, sec_button } from '../../app/components/Styles';
import { brandFilterOptions, brandOptionLabel } from '../../app/components/BrandSelect';
import { getVendors, getStockRecounts } from '../../app/store/actions/imsAdminActions.js';
import { AddCircleOutline, Visibility } from '@mui/icons-material';
import { useEffect } from 'react';
import { Table } from 'antd';
import { antTableWrapperSx, tableShowTotal, idTableColumn } from '../../app/components/AntTableStyles';
import moment from 'moment';

const TASK_STATUS_COLORS = {
    processing: 'warning',
    completed: 'success',
    failed: 'error',
};

const formatTaskStatus = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
};

export const StockRecountsList = ({
    getVendors,
    getStockRecounts,
    vendors,
    stockRecounts,
    isLoading,
}) => {
    const navigate = useNavigate();
    const [vendorId, setVendorId] = React.useState(null);
    const [page, setPage] = React.useState(1);
    const limit = 20;

    useEffect(() => {
        getVendors({ page: 1, limit: 500 });
    }, []);

    useEffect(() => {
        getStockRecounts({
            page,
            limit,
            ...(vendorId ? { vendorId } : {}),
        });
    }, [page, vendorId]);

    const selectedVendor = React.useMemo(
        () => vendors?.find((vendor) => vendor._id === vendorId) || null,
        [vendors, vendorId]
    );

    const columns = [
        idTableColumn(),
        {
            title: 'Brand',
            dataIndex: 'brandName',
            sorter: (a, b) => (a.brandName || '').localeCompare(b.brandName || ''),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status) => (
                <Chip
                    label={formatTaskStatus(status)}
                    color={TASK_STATUS_COLORS[status] || 'default'}
                    size="small"
                />
            ),
        },
        {
            title: 'Products',
            dataIndex: 'total',
            render: (_, row) => row.summary?.total ?? 0,
        },
        {
            title: 'Updated',
            dataIndex: 'success',
            render: (_, row) => (
                <Typography component="span" color="success.main" fontWeight={600}>
                    {row.summary?.success ?? 0}
                </Typography>
            ),
        },
        {
            title: 'Failed',
            dataIndex: 'failed',
            render: (_, row) => (
                <Typography
                    component="span"
                    color={row.summary?.failed > 0 ? 'error.main' : 'text.secondary'}
                    fontWeight={row.summary?.failed > 0 ? 600 : 400}
                >
                    {row.summary?.failed ?? 0}
                </Typography>
            ),
        },
        {
            title: 'Skipped',
            dataIndex: 'skipped',
            render: (_, row) => row.summary?.skipped ?? 0,
        },
        {
            title: 'Started By',
            dataIndex: 'adminName',
        },
        {
            title: 'Started At',
            dataIndex: 'createdAt',
            render: (date) => moment(date).format('DD MMM YYYY, HH:mm'),
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Actions',
            dataIndex: '_id',
            render: (id) => (
                <Button
                    size="small"
                    startIcon={<Visibility />}
                    sx={sec_button}
                    onClick={() => navigate(`/ims/admin/stock-recount/progress/${id}`)}
                >
                    View
                </Button>
            ),
        },
    ];

    const data = (stockRecounts?.docs || []).map((row) => ({
        _id: row._id,
        brandName: row.vendor?.brandName || '—',
        status: row.status,
        summary: row.summary,
        adminName: row.admin?.name || row.admin?.email || '—',
        createdAt: row.createdAt,
    }));

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Typography variant="h4" sx={{ color: 'var(--secColor)' }}>
                    Stock Recounts
                </Typography>
                <Button
                    sx={main_button}
                    startIcon={<AddCircleOutline />}
                    onClick={() => navigate('/ims/admin/stock-recount')}
                >
                    New Recount
                </Button>
            </Box>

            <Box sx={{ mb: 3, maxWidth: 400 }}>
                {vendors && (
                    <Autocomplete
                        options={vendors}
                        value={selectedVendor}
                        onChange={(_, value) => {
                            setVendorId(value ? value._id : null);
                            setPage(1);
                        }}
                        getOptionLabel={brandOptionLabel}
                        filterOptions={brandFilterOptions}
                        renderInput={(params) => (
                            <MainInput
                                {...params}
                                margin="normal"
                                fullWidth
                                label="Filter by brand"
                            />
                        )}
                    />
                )}
            </Box>

            {isLoading && !stockRecounts?.docs?.length ? (
                <Box sx={{ ...centered_flex_box, py: 6 }}>
                    <CircularProgress sx={{ color: 'var(--secColor)' }} />
                </Box>
            ) : (
                <Box sx={antTableWrapperSx}>
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="_id"
                        loading={isLoading}
                        pagination={{
                            current: page,
                            pageSize: limit,
                            total: stockRecounts?.totalDocs || 0,
                            showTotal: tableShowTotal,
                            onChange: (nextPage) => setPage(nextPage),
                        }}
                        onRow={(record) => ({
                            onClick: () => navigate(`/ims/admin/stock-recount/progress/${record._id}`),
                            style: { cursor: 'pointer' },
                        })}
                    />
                </Box>
            )}
        </Container>
    );
};

const mapStateToProps = (state) => ({
    isLoading: state?.records?.isLoading,
    vendors: state?.records?.vendors?.docs,
    stockRecounts: state?.records?.stockRecounts,
});

const mapDispatchToProps = { getVendors, getStockRecounts };

export default connect(mapStateToProps, mapDispatchToProps)(StockRecountsList);
